import {
  Inbox, ListTodo, Activity, Bug, Sparkles as Lead,
  Calendar, ExternalLink, Type, FileCheck, Blocks, FileJson, Grid3X3, Wand2,
  Users, UserCog, Mail, Crown, HandCoins,
  ShoppingBag, CreditCard, Package, Bed, Gift, Tag, Library, FileText, Building2,
  Megaphone, Link2, MessageCircle, BarChart3, Code, Globe, Trophy,
  Star, Radar,
  KeyRound, Settings, Image as ImageIcon, ShieldCheck, Bot, Zap, Share2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AdminMode } from '@/components/admin/AdminModeToggle';

/**
 * Single source of truth for the entire admin navigation tree.
 *
 * Both the sidebar (AdminSidebar) and the command palette (AdminCommandPalette)
 * render from this array, so labels, grouping and available destinations can
 * never diverge between the two navigations again.
 *
 * A destination is either a stand-alone route ("/admin/...") or a tab inside
 * one of the dashboard mode panels ({ mode, tab }). Tab targets are turned into
 * "?mode=<mode>&tab=<tab>" URLs by the consumers.
 */

export type AdminNavBadge =
  | 'pendingApplications'
  | 'pendingEventDrafts'
  | 'pendingOrders'
  | 'unreadMessages'
  | 'openLeads'
  | 'activeAffiliates';

export type AdminNavTarget =
  | { route: string }
  | { mode: AdminMode; tab?: string };

export interface AdminNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  keywords: string[];
  target: AdminNavTarget;
  badge?: AdminNavBadge;
}

export interface AdminNavSection {
  id: string;
  label: string;
  icon: LucideIcon;
  items: AdminNavItem[];
}

export const ADMIN_NAV: AdminNavSection[] = [
  {
    id: 'operations',
    label: 'Master Menu',
    icon: Inbox,
    // Daily hub — only things that need attention day-to-day.
    items: [
      { id: 'ops-inbox', label: 'Inbox', icon: Inbox, target: { route: '/admin/inbox' }, keywords: ['inbox', 'messages', 'unread'], badge: 'unreadMessages' },
      { id: 'ops-leads', label: 'Leads', icon: Lead, target: { route: '/admin/leads' }, keywords: ['lead', 'contact', 'crm'], badge: 'openLeads' },
      { id: 'ops-orders', label: 'Orders', icon: CreditCard, target: { route: '/admin/orders' }, keywords: ['order', 'purchase', 'sale', 'payment', 'invoice'], badge: 'pendingOrders' },
      { id: 'ops-applications', label: 'Applications', icon: FileCheck, target: { mode: 'organization', tab: 'applications' }, keywords: ['application', 'speaker', 'volunteer', 'ambassador', 'pending'], badge: 'pendingApplications' },
      { id: 'ops-affiliates', label: 'Event Affiliates', icon: HandCoins, target: { mode: 'organization', tab: 'affiliates' }, keywords: ['affiliate', 'partner', 'commission', 'per-event', 'referral'], badge: 'activeAffiliates' },
      { id: 'ops-guestlist', label: 'Guest List', icon: Gift, target: { mode: 'organization', tab: 'guest-list' }, keywords: ['guest', 'free ticket', 'comp', 'invite', 'gratis', 'complimentary'] },
      { id: 'ops-tasks', label: 'Team Tasks', icon: ListTodo, target: { route: '/admin/tasks' }, keywords: ['task', 'todo', 'team'] },
    ],
  },
  {
    id: 'events',
    label: 'Events Creation',
    icon: Calendar,
    items: [
      { id: 'ev-all', label: 'All Events', icon: Calendar, target: { mode: 'events', tab: 'all-events' }, keywords: ['event', 'all', 'manage', 'list'] },
      { id: 'ev-external', label: 'External Events', icon: ExternalLink, target: { mode: 'events', tab: 'external-events' }, keywords: ['external', 'event', 'partner'] },
      { id: 'ev-content', label: 'Event Content', icon: Type, target: { mode: 'events', tab: 'content' }, keywords: ['content', 'page', 'edit'] },
      { id: 'ev-drafts', label: 'Drafts', icon: FileCheck, target: { mode: 'events', tab: 'drafts' }, keywords: ['draft', 'pending', 'review'], badge: 'pendingEventDrafts' },
      { id: 'ev-templates', label: 'Templates', icon: Blocks, target: { mode: 'events', tab: 'templates' }, keywords: ['template', 'master'] },
      { id: 'ev-wizard', label: 'AI Event Wizard', icon: Wand2, target: { route: '/admin/ai-event-wizard' }, keywords: ['ai', 'wizard', 'create', 'generate'] },
      { id: 'ev-json', label: 'JSON Event Creator', icon: FileJson, target: { route: '/admin/json-event-creator' }, keywords: ['json', 'import', 'data'] },
      { id: 'ev-import', label: 'Import Event', icon: ExternalLink, target: { route: '/admin/import-event' }, keywords: ['import', 'external', 'add'] },
      { id: 'ev-scraper', label: 'Nomad Fest Scraper', icon: Radar, target: { route: '/admin/event-scraper' }, keywords: ['scraper', 'scrape', 'crawl', 'discover', 'monitor', 'external', 'auto', 'update', 'sync'] },
      { id: 'ev-blocks', label: 'Block Builder Studio', icon: Grid3X3, target: { route: '/admin/block-builder' }, keywords: ['block', 'builder', 'design', 'page'] },
    ],
  },
  {
    id: 'organization',
    label: 'Events Organization',
    icon: Users,
    // Order mirrors the panel's grouped tabs: People → Comms → Growth.
    items: [
      { id: 'org-apps', label: 'Applications', icon: FileCheck, target: { mode: 'organization', tab: 'applications' }, keywords: ['application', 'speaker', 'volunteer', 'ambassador', 'apply', 'people'], badge: 'pendingApplications' },
      { id: 'org-teams', label: 'Event Teams', icon: UserCog, target: { mode: 'organization', tab: 'event-teams' }, keywords: ['team', 'crew', 'staff', 'people'] },
      { id: 'org-coorg', label: 'Co-Organizers', icon: Crown, target: { mode: 'organization', tab: 'co-organizers' }, keywords: ['co-organizer', 'referral', 'goal', 'people'] },
      { id: 'org-inbox', label: 'Organizer Inbox', icon: Inbox, target: { mode: 'organization', tab: 'inbox' }, keywords: ['inbox', 'organizer', 'messages', 'comms'] },
      { id: 'org-emails', label: 'Email Templates', icon: Mail, target: { mode: 'organization', tab: 'email-templates' }, keywords: ['email', 'template', 'approval', 'comms'] },
      { id: 'org-affiliates', label: 'Event Affiliates', icon: HandCoins, target: { mode: 'organization', tab: 'affiliates' }, keywords: ['affiliate', 'partner', 'commission', 'per-event', 'growth'], badge: 'activeAffiliates' },
      { id: 'org-earlybird', label: 'Early Bird', icon: Tag, target: { mode: 'organization', tab: 'discounts' }, keywords: ['early bird', 'campaign', 'pricing', 'per-event', 'tier', 'growth'] },
    ],
  },
  {
    id: 'commerce',
    label: 'Shop & Tickets',
    icon: ShoppingBag,
    items: [
      { id: 'com-inventory', label: 'Inventory', icon: Package, target: { mode: 'commerce', tab: 'inventory' }, keywords: ['inventory', 'stock', 'quantity'] },
      { id: 'com-orders', label: 'Orders', icon: CreditCard, target: { mode: 'commerce', tab: 'orders' }, keywords: ['order', 'payment', 'sale', 'booking'] },
      { id: 'com-products', label: 'Products', icon: ShoppingBag, target: { mode: 'commerce', tab: 'products' }, keywords: ['product', 'item', 'ticket'] },
      { id: 'com-accommodations', label: 'Accommodations', icon: Bed, target: { mode: 'commerce', tab: 'accommodations' }, keywords: ['accommodation', 'room', 'venue', 'hotel', 'bed'] },
      { id: 'com-bundles', label: 'Bundles', icon: Gift, target: { mode: 'commerce', tab: 'bundles' }, keywords: ['bundle', 'package', 'combo'] },
      { id: 'com-discounts', label: 'Discounts', icon: Tag, target: { mode: 'commerce', tab: 'discounts' }, keywords: ['discount', 'promo', 'code', 'early bird', 'campaign'] },
      { id: 'com-library', label: 'Product Library', icon: Library, target: { route: '/admin/product-library' }, keywords: ['product', 'library', 'catalog', 'shared'] },
      { id: 'com-invoices', label: 'Invoices', icon: FileText, target: { route: '/admin/invoices' }, keywords: ['invoice', 'bill', 'receipt'] },
      { id: 'com-bank', label: 'Bank Settings', icon: Building2, target: { route: '/admin/bank-settings' }, keywords: ['bank', 'payout', 'iban'] },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    items: [
      { id: 'mkt-subscribers', label: 'Subscribers', icon: Mail, target: { mode: 'marketing', tab: 'subscribers' }, keywords: ['subscriber', 'newsletter', 'email list'] },
      { id: 'mkt-affiliates', label: 'Affiliates', icon: Users, target: { mode: 'marketing', tab: 'affiliates' }, keywords: ['affiliate', 'partner', 'commission', 'global'], badge: 'activeAffiliates' },
      { id: 'mkt-links', label: 'Links', icon: Link2, target: { mode: 'marketing', tab: 'links' }, keywords: ['link', 'tester', 'utm'] },
      { id: 'mkt-blog', label: 'Blog', icon: FileText, target: { mode: 'marketing', tab: 'blog' }, keywords: ['blog', 'post', 'article', 'content'] },
      { id: 'mkt-partners', label: 'Partners', icon: Users, target: { mode: 'marketing', tab: 'partners' }, keywords: ['partner', 'sponsor'] },
      { id: 'mkt-chatbot', label: 'Chatbot', icon: MessageCircle, target: { mode: 'marketing', tab: 'chatbot' }, keywords: ['chatbot', 'ai', 'knowledge', 'conversation'] },
      { id: 'mkt-hub', label: 'Marketing Hub', icon: BarChart3, target: { route: '/admin/marketing' }, keywords: ['marketing', 'schedule', 'social', 'campaign', 'analytics'] },
      { id: 'mkt-loyalty', label: 'Loyalty Program', icon: Gift, target: { route: '/admin/loyalty' }, keywords: ['loyalty', 'points', 'reward'] },
      { id: 'mkt-community', label: 'Community Rewards', icon: Megaphone, target: { route: '/admin/community-rewards' }, keywords: ['community', 'engagement', 'reward'] },
      { id: 'mkt-competitions', label: 'Competitions', icon: Trophy, target: { route: '/admin/competitions' }, keywords: ['competition', 'contest', 'entry'] },
      { id: 'mkt-embed', label: 'Embed Manager', icon: Code, target: { route: '/admin/embed-manager' }, keywords: ['embed', 'widget', 'iframe'] },
      { id: 'mkt-seo', label: 'SEO Landing Pages', icon: Globe, target: { route: '/admin/seo-landing-pages' }, keywords: ['seo', 'meta', 'landing', 'sharing'] },
    ],
  },
  {
    id: 'community',
    label: 'Community',
    icon: Building2,
    items: [
      { id: 'cmy-talent', label: 'Talent Pool', icon: Star, target: { mode: 'community', tab: 'talent-pool' }, keywords: ['talent', 'pool', 'speaker', 'directory'] },
      { id: 'cmy-orgs', label: 'Organizations', icon: Building2, target: { mode: 'community', tab: 'organizations' }, keywords: ['organization', 'company', 'org'] },
    ],
  },
  {
    id: 'system',
    label: 'Settings',
    icon: Settings,
    items: [
      { id: 'sys-access', label: 'Access & Users', icon: KeyRound, target: { mode: 'system', tab: 'access' }, keywords: ['user', 'access', 'role', 'permission'] },
      { id: 'sys-config', label: 'Configuration', icon: Settings, target: { mode: 'system', tab: 'configuration' }, keywords: ['config', 'email', 'smtp', 'notification', 'page visibility'] },
      { id: 'sys-content', label: 'Content Tools', icon: ImageIcon, target: { mode: 'system', tab: 'content' }, keywords: ['content', 'image', 'awards', 'participation'] },
      { id: 'sys-monitoring', label: 'Monitoring', icon: ShieldCheck, target: { mode: 'system', tab: 'monitoring' }, keywords: ['monitor', 'rate limit', 'ai credits'] },
      { id: 'sys-health', label: 'System Health', icon: Activity, target: { route: '/admin/health' }, keywords: ['health', 'monitor', 'status', 'uptime', 'alert'] },
      { id: 'sys-activity', label: 'Activity Feed', icon: Activity, target: { route: '/admin/live' }, keywords: ['activity', 'live', 'audit', 'log'] },
      { id: 'sys-bugs', label: 'Bug Reports', icon: Bug, target: { route: '/admin/bug-reports' }, keywords: ['bug', 'report', 'issue'] },
      { id: 'sys-ai', label: 'AI Providers', icon: Bot, target: { route: '/admin/ai-settings' }, keywords: ['ai', 'provider', 'api key', 'model'] },
      { id: 'sys-image', label: 'AI Image Studio', icon: Zap, target: { route: '/admin/ai-image-studio' }, keywords: ['ai', 'image', 'generate', 'photo'] },
      { id: 'sys-social', label: 'Social Settings', icon: Share2, target: { route: '/admin/social-settings' }, keywords: ['social', 'facebook', 'instagram', 'buffer', 'linkedin'] },
    ],
  },
];

/** Modes reachable through the mode panels (used to validate ?mode= params). */
export const ADMIN_MODES: AdminMode[] = ['events', 'organization', 'commerce', 'marketing', 'community', 'system'];

/** Resolve a nav target to a URL the router can navigate to. */
export function adminNavHref(target: AdminNavTarget): string {
  if ('route' in target) return target.route;
  const params = new URLSearchParams({ mode: target.mode });
  if (target.tab) params.set('tab', target.tab);
  return `/admin?${params.toString()}`;
}
